import { Api } from 'src/api';
import { InterceptableAxiosRequestConfig, isCancelError } from 'src/api/Http/Http.types';
import { download } from 'src/lib/utils/download';
import { Logger } from 'src/utils/logger';

// The caller owns the failure toast, so Http's global one is suppressed —
// same reasoning as useMediaDownload.
const noGlobalToast: InterceptableAxiosRequestConfig = { _noIntercept: true };

/**
 * Saves one Space media file, resolving its URL through
 * `GET /media/{id}/download-url` instead of fetching the storage URL directly.
 *
 * Going through the API is what puts the download in the request's activity
 * log as dispute evidence (#2784); a direct storage fetch is invisible to the
 * backend. The file is still saved with `download()` (fetch + blob, iOS share
 * sheet), so callers that used it keep behaving the same on every platform.
 *
 * A 409 is the API refusing the file (cold storage, unfinished upload) and is
 * re-thrown — its message is written for the customer. Any other API failure
 * falls back to the direct URL when one is given, so an API hiccup never
 * costs a client their download; that one download simply goes unlogged.
 */
export const downloadMediaViaApi = async (
  mediaId: string,
  fileName?: string,
  fallbackUrl?: string,
): Promise<{ shared: boolean }> => {
  let url: string;

  try {
    ({ download_url: url } = await Api.media.getDownloadUrl(mediaId, noGlobalToast));
  } catch (error) {
    const { code } = (error ?? {}) as { code?: number | string };

    // A cancelled request (e.g. logout mid-click) is not an outage: don't
    // go on to download behind the user's back.
    if (
      code === 409 ||
      !fallbackUrl ||
      isCancelError((error ?? {}) as { code?: number | string })
    ) {
      throw error;
    }

    Logger.warn('download-url failed, falling back to the direct URL', 'downloadMediaViaApi', {
      mediaId,
      error,
    });
    url = fallbackUrl;
  }

  return download(url, fileName);
};
